import type { Channel, ReportQueryRequest, ReportQueryResponse } from '@/api/types';
import { fmtDateInput, nyStartOfDayMs } from '@/lib/datetime';
import type { Instant } from '@/lib/datetime';
import { directPerformanceMetrics } from '@/lib/performance-metrics';
import type { DirectPerformanceMetrics } from '@/lib/performance-metrics';

/** The forecast's history: the last 180 closed New York days. */
export const CHANNEL_BENCHMARK_DAYS = 180;

const HALF_DAY_MS = 43_200_000;

/**
 * The 180 closed New York days the channel tiles and the forecast read,
 * yesterday last. Walked by NY midnights so DST never shifts a day.
 */
export function channelBenchmarkWindow(now: Instant = Date.now()): { from: string; to: string } {
  const todayStart = nyStartOfDayMs(now);
  let fromStart = todayStart;
  for (let day = 0; day < CHANNEL_BENCHMARK_DAYS; day += 1) {
    fromStart = nyStartOfDayMs(fromStart - HALF_DAY_MS);
  }
  return { from: fmtDateInput(fromStart), to: fmtDateInput(todayStart - 1) };
}

/**
 * Reports → Performance by channel over that window: direct sales only, USD,
 * for one production or, before one is picked, every production the reader
 * may see. The tiles quote exactly these figures.
 */
export function channelBenchmarkRequest(
  productionId: string | null,
  window: { from: string; to: string },
): ReportQueryRequest {
  return {
    dataset: 'performance',
    attribution_scope: 'direct_only',
    date_from: window.from,
    date_to: window.to,
    group_by: ['channel'],
    filters: {
      assigned_production_ids: productionId ? [productionId] : [],
      sold_production_ids: [],
      campaign_ids: [],
      audience_ids: [],
      audience_revision: null,
      creative_ids: [],
      creative_revision: null,
      channels: [],
      audience_types: [],
      targets: [],
      placements: [],
      currencies: ['USD'],
      provider_ad_ids: [],
      provider_creative_ids: [],
      attribution_effects: [],
      attribution_settings: [],
    },
    order: null,
    cursor: null,
    limit: 500,
  };
}

/** Each channel's direct metrics; a channel with no delivery is absent. */
export function channelBenchmarks(
  response: Pick<ReportQueryResponse, 'rows'> | undefined,
): Map<Channel, DirectPerformanceMetrics> {
  const out = new Map<Channel, DirectPerformanceMetrics>();
  for (const row of response?.rows ?? []) {
    const channel = row.dimensions.channel;
    if (!channel) continue;
    const metrics = directPerformanceMetrics(row.metrics);
    if (metrics.gross_spend_cents <= 0 && metrics.impressions <= 0) continue;
    out.set(channel, metrics);
  }
  return out;
}
